import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { UploadNewAoiRequestPlayload, CFCProps } from 'types';
import {
  uploadShapeFileStart,
  uploadShapeFileFailure
} from 'redux/areaOfInterest/actions';
import {
  TextInput,
  Button,
  InlineNotification,
  Loading,
  Select,
  SelectItem,
  FileUploaderButton
} from 'carbon-components-react';
import i18n from 'i18n';

const fileTypeOption = [
  { label: 'ESRI Shapefile', value: 'shp2db' },
  { label: 'MapInfo TAB', value: 'mitab2db' },
  { label: 'KMZ', value: 'kmz2db' }
];

const geometryTypeOption = [
  { label: 'Polygon', value: 'Polygon' },
  { label: 'Line', value: 'LineString' }
];

const bufferSizeOption = [
  { label: '10 metres', value: 10 },
  { label: '20 metres', value: 20 },
  { label: '50 metres', value: 50 },
  { label: '100 metres', value: 100 },
  { label: '200 metres', value: 200 }
];

const UploadPanel: React.FC<Props> = ({
  uploadShapeFile,
  previewIdFromFile,
  uploadState,
  errMsg,
  onPanelClose
}) => {
  const [shapeFile, setShapeFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<string>('-');
  const [geometryType, setGeometryType] = useState<string>('-');
  const [bufferDistance, setBufferDistance] = useState<number>(20);
  const dispatch: any = useDispatch();
  const acceptedMimeType = [
    'application/zip',
    'application/octet-stream',
    'application/x-zip-compressed',
    'multipart/x-zip'
  ];

  const optimizeErrMsg = (
    errMsg: string | { msg: string; details: Record<string, any> }
  ) => {
    if (typeof errMsg === 'string') {
      return i18n.exists(`AOI.upload.error.${errMsg}`)
        ? i18n.t(`AOI.upload.error.${errMsg}`)
        : errMsg;
    } else {
      if (errMsg.msg === 'MISSING_FILES_IN_ZIP') {
        const ext = Array.isArray(errMsg.details?.missingFiles)
          ? `: [${errMsg.details?.missingFiles.join(',')}]`
          : '';
        return i18n.exists(`AOI.upload.error.${errMsg.msg}`)
          ? `${i18n.t(`AOI.upload.error.${errMsg.msg}`)}${ext}`
          : errMsg.msg;
      } else {
        return i18n.exists(`AOI.upload.error.${errMsg.msg}`)
          ? i18n.t(`AOI.upload.error.${errMsg.msg}`)
          : errMsg.msg;
      }
    }
  };

  const isZip = (type: string) => {
    return acceptedMimeType.includes(type);
  };

  const isKMZ = (name: string) => {
    const ext = name.substr(name.lastIndexOf('.'));
    return ext && ext.toLowerCase() === '.kmz';
  };

  const isCorrectFileType = (shapeFile: File, fileType: string) => {
    return fileType === 'kmz2db'
      ? isKMZ(shapeFile.name)
      : isZip(shapeFile.type);
  };

  const uploadNewAoi = async () => {
    if (!shapeFile || !isCorrectFileType(shapeFile, fileType)) {
      return dispatch(uploadShapeFileFailure('Invalid file type'));
    }

    const payload: UploadNewAoiRequestPlayload = {
      shapeFile,
      fileType,
      geometryType,
      bufferDistance,
      mimeType: 'application/zip'
    };
    dispatch(uploadShapeFileStart(payload));
  };

  const cancel = async () => {
    onPanelClose();
  };

  const onShapeFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const target = evt.target;
    if (target.files) {
      setShapeFile(target.files[0]);
    } else {
      setShapeFile(null);
    }
  };

  useEffect(() => {
    setShapeFile(uploadShapeFile);
  }, [uploadShapeFile]);

  useEffect(() => {
    if (previewIdFromFile) {
      onPanelClose();
    }
  }, [previewIdFromFile, onPanelClose]);

  return (
    <>
      <div className="aoi-right-panel --swx-theme--dark">
        <div className="panel-description">
          This system uses this GIS data to define your Area of Interest and
          notify you when potential work impacts your infrastructure
        </div>
        {uploadState === 'processing' && (
          <div className="loading-container panel-divider">
            <Loading />
          </div>
        )}
        {uploadState !== 'processing' && (
          <>
            <div className="file-type-selector panel-divider">
              <Select
                defaultValue={fileType}
                id="aoi-file-type-select"
                labelText="File type"
                onChange={(evt: any) => setFileType(evt.target.value)}
              >
                <SelectItem
                  disabled
                  hidden
                  value="-"
                  text="Choose a file type ..."
                />
                {fileTypeOption.map((fileType: any, inx: number) => {
                  return (
                    <SelectItem
                      key={`file-type-${inx}`}
                      text={fileType.label}
                      value={fileType.value}
                    />
                  );
                })}
              </Select>
              <Select
                defaultValue={geometryType}
                id="aoi-geometry-type-select"
                labelText="Geometry"
                onChange={(evt: any) => setGeometryType(evt.target.value)}
              >
                <SelectItem
                  disabled
                  hidden
                  value="-"
                  text="Choose a geometry type ..."
                />
                {geometryTypeOption.map((geometryType: any, inx: number) => {
                  return (
                    <SelectItem
                      key={`geo-type-${inx}`}
                      text={geometryType.label}
                      value={geometryType.value}
                    />
                  );
                })}
              </Select>
              {geometryType === 'LineString' && (
                <Select
                  defaultValue={bufferDistance}
                  id="aoi-buffer-size-select"
                  labelText="Buffer"
                  onChange={(evt: any) => setBufferDistance(evt.target.value)}
                >
                  {bufferSizeOption.map((bufferSize: any, inx: number) => {
                    return (
                      <SelectItem
                        key={`buffer-size-${inx}`}
                        text={bufferSize.label}
                        value={bufferSize.value}
                      />
                    );
                  })}
                </Select>
              )}
            </div>
            {fileType !== '-' && geometryType !== '-' && (
              <div className="file-control panel-divider">
                <TextInput
                  id="shapefile-upload-readonly-input"
                  invalidText="A valid value is required"
                  labelText="Upload file"
                  placeholder="Click browse to select file"
                  value={shapeFile?.name || ''}
                  readOnly={true}
                />
                <FileUploaderButton
                  buttonKind="ghost"
                  labelText="Browse …"
                  size="small"
                  disableLabelChanges={true}
                  onChange={onShapeFileChange}
                  accept={fileType === 'kmz2db' ? ['.kmz', '.zip'] : ['.zip']}
                />
                {uploadState === 'fail' && errMsg && (
                  <InlineNotification
                    className="aoi-upload-panel--error-msg"
                    kind="error"
                    title={'Error'}
                    subtitle={optimizeErrMsg(errMsg)}
                    lowContrast
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="aoi-right-panel-button-container">
        <Button
          kind="secondary"
          onClick={() => {
            cancel();
          }}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={() => {
            uploadNewAoi();
          }}
          disabled={!shapeFile}
        >
          Preview
        </Button>
      </div>
    </>
  );
};

type ownProps = {
  onPanelClose: () => void;
};

const mapStateToProps = ({ aoiCache }: RootState) => ({
  uploadShapeFile: aoiCache.shapeFile,
  previewIdFromFile: aoiCache.previewIdFromFile,
  uploadState: aoiCache.uploadState,
  errMsg: aoiCache.errMsg
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector> & ownProps;

UploadPanel.displayName = 'AOIShapeFileUploadPanel';

export default React.memo(connector(UploadPanel));
